/**
 * Per-station coverage as five colour bands — the chart's coverage strip and
 * the table's coverage cell. Replaced the low-coverage hatch / strike-through
 * (2026-10-05, client request): a reader sees HOW covered a station is at a
 * glance, not only whether it crossed `minCoveragePct`.
 *
 * Fixed at 20/40/60/80 %, deliberately independent of the run's
 * `minCoveragePct` — that threshold still drives the ⚠ flag and the
 * "unreliable" footnote, which are what survive a greyscale print.
 *
 * Twin: `server/background/src/functions/processReport/coverageBands.ts`
 * (the PDF). Same boundaries, same colours — change both.
 */

export interface CoverageBand {
  index: 0 | 1 | 2 | 3 | 4;
  name: 'red' | 'orange' | 'yellow' | 'lightGreen' | 'darkGreen';
  /** Lower bound, inclusive, in percent. */
  minPct: number;
  color: string;
  /** Text ink on `color` (the table cell). */
  ink: string;
}

export const COVERAGE_BANDS: readonly CoverageBand[] = [
  { index: 0, name: 'red', minPct: 0, color: '#d64541', ink: '#1a1a19' },
  { index: 1, name: 'orange', minPct: 20, color: '#f08a24', ink: '#1a1a19' },
  { index: 2, name: 'yellow', minPct: 40, color: '#f2c94c', ink: '#1a1a19' },
  { index: 3, name: 'lightGreen', minPct: 60, color: '#9ccc65', ink: '#1a1a19' },
  { index: 4, name: 'darkGreen', minPct: 80, color: '#2e7d32', ink: '#ffffff' },
];

/** The band for a coverage percentage. Missing / non-finite → the lowest. */
export function coverageBand(pct: number | null | undefined): CoverageBand {
  const p = typeof pct === 'number' && Number.isFinite(pct) ? pct : 0;
  for (let i = COVERAGE_BANDS.length - 1; i > 0; i--) if (p >= COVERAGE_BANDS[i].minPct) return COVERAGE_BANDS[i];
  return COVERAGE_BANDS[0];
}

/**
 * Whether a bin has reference cells to be covered — reference cells on a run
 * with `coverageBasis`, corridor cells before it. Unknown counts as yes.
 */
export function hasReference(b: { refCellCount?: number | null; binCellCount?: number | null }): boolean {
  const w = b.refCellCount ?? b.binCellCount;
  return typeof w === 'number' ? w > 0 : true;
}

/**
 * Low coverage, counting a station the reference surface never reaches.
 * `station_bins.py` only flagged a bin with reference cells (an empty bin was
 * "nothing to measure"); since 2026-10-06 (client request) an empty station
 * is uncovered alignment — 0 %, and low whenever there is a threshold at all.
 * Applied on read, so runs made before the worker changed read the same.
 */
export function isLowCoverage(
  b: { lowCoverage: boolean; coveragePct: number; refCellCount?: number | null; binCellCount?: number | null },
  minCoveragePct: number,
): boolean {
  return b.lowCoverage || (!hasReference(b) && b.coveragePct < minCoveragePct);
}

/**
 * Coverage ALONG THE ALIGNMENT over a run of station intervals: each
 * interval's own coverage weighted by its length, an interval with no
 * reference surface counting as 0 %. A design that ends halfway along the
 * alignment is then 50 % covered, not 100 % (2026-10-06, client request —
 * the reference-cell-weighted mean gave such an interval no weight at all).
 * `null` when the run has no length.
 */
export function alignmentCoveragePct(
  bins: readonly { staStartInternalM: number; staEndInternalM: number; coveragePct: number }[],
): number | null {
  let acc = 0;
  let len = 0;
  for (const b of bins) {
    const l = b.staEndInternalM - b.staStartInternalM;
    if (!(l > 0)) continue;
    acc += (Number.isFinite(b.coveragePct) ? b.coveragePct : 0) * l;
    len += l;
  }
  return len > 0 ? acc / len : null;
}
